import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { CreditModeProvider, useCreditMode } from './CreditModeContext';
import { monthOptions } from './aiUsageShared';
import {
  mapPlatformSummary,
  mapOrgSummaryRows,
  mapLeaderboard,
  LEADERBOARD_SORTS,
} from './creditFieldMap';
import {
  getPlatformSummary,
  getOrgsSummary,
  getLeaderboard,
} from '../../services/api/eiCreditsService';
import {
  currentPeriod,
  monthLabel,
  formatCredits,
  formatCost,
  formatUsd,
} from '../../utils/creditFormat';
import {
  sa,
  DataTable,
  KpiBar,
  ProgressBar,
  TypeBadge,
  PillButton,
  SelectPill,
  CreditCostToggle,
  SectionHeaderRow,
  SectionStates,
} from '../superAdminUi';

// Platform-wide AI usage overview that leads the Super Admin dashboard (Figma "Dashboard (AI
// Usage)"): four headline KPIs, a per-organisation Platform Usage table, and a Schools
// Leaderboard. Self-contained — owns its own period + Credit/Cost toggle, takes no props.

// The orgs/summary endpoint pages server-side; one request returns this many ranked rows.
const PAGE_SIZE = 25;

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${sa.space.section};
  width: 100%;
`;

const Pager = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 12px;
  font-size: 13px;
  color: ${sa.color.textGray};
`;

const PagerBtn = styled.button`
  min-width: 72px;
  height: 34px;
  padding: 0 14px;
  border: 1px solid ${sa.color.stroke};
  border-radius: ${sa.radius.sm};
  background: ${sa.color.white};
  color: ${sa.color.text};
  font-family: ${sa.font.family};
  font-size: 13px;
  cursor: pointer;
  &:disabled {
    opacity: 0.45;
    cursor: default;
  }
`;

const OrgName = styled.span`
  font-weight: ${sa.font.semibold};
  color: ${sa.color.text};
`;
const Teal = styled.span`
  font-weight: ${sa.font.bold};
  color: ${sa.color.teal};
`;

// Where the "View" drill-in goes (the per-trust overview): groups open the group account view,
// standalone schools the school one. The orgs/summary row exposes the resolved org id; prefer an
// explicit group/school id when present.
const drillPath = (row) => {
  if (row.type === 'GROUP') return `/group-detail?matId=${row.groupId != null ? row.groupId : row.id}`;
  const id = row.schoolId != null ? row.schoolId : row.id;
  return `/school-detail?schoolId=${id}`;
};

function PlatformInner() {
  const history = useHistory();
  const { mode, setMode, format } = useCreditMode();

  const [period, setPeriod] = useState(currentPeriod());
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [offset, setOffset] = useState(0);
  const [leadersRaw, setLeadersRaw] = useState(null);
  const [sortKey, setSortKey] = useState('messages'); // design default: "Messages Sent"
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const months = useMemo(
    () => monthOptions().map((p) => ({ value: p, label: monthLabel(p) })),
    []
  );
  const sortOptions = useMemo(
    () => LEADERBOARD_SORTS.map((o) => ({ value: o.key, label: o.label })),
    []
  );

  // The Platform Usage list is a server-side ranked, paginated org rollup (fast: no per-school
  // fan-out); the headline summary is a separate single-figure call. Both re-query on period; the
  // list also re-queries on page (offset). PAGE_SIZE is the server `limit` — a full page implies
  // there may be more, which drives the Next button (the endpoint returns no total).
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [summaryRaw, orgsRaw] = await Promise.all([
        getPlatformSummary(period),
        getOrgsSummary({ period, sortBy: 'usage', order: 'desc', limit: PAGE_SIZE, offset }),
      ]);
      setSummary(mapPlatformSummary(summaryRaw));
      setRows(mapOrgSummaryRows(orgsRaw));
    } catch (err) {
      setError(err?.message || 'Could not load platform AI usage for this period.');
      setSummary(null);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [period, offset]);

  useEffect(() => {
    load();
  }, [load]);

  // A period change resets to the first page (offset lives in its own state so paging doesn't
  // re-fetch the summary needlessly, but a new period must start from the top).
  useEffect(() => {
    setOffset(0);
  }, [period]);

  // Leaderboard is ordered server-side by the chosen metric; changing the Sort-by dropdown (or
  // the period) re-queries. An unknown metric 422s — fall back to the default so it still renders.
  const loadLeaderboard = useCallback(async () => {
    try {
      const raw = await getLeaderboard({ scope: 'school', period, sortBy: sortKey });
      setLeadersRaw(raw);
    } catch (err) {
      if (err?.status === 422 && sortKey !== 'messages') {
        setSortKey('messages');
        return;
      }
      setLeadersRaw(null);
    }
  }, [period, sortKey]);

  useEffect(() => {
    loadLeaderboard();
  }, [loadLeaderboard]);

  // Names are resolved at render, not at fetch: the per-customer rollup arrives from a separate
  // request, and re-mapping when it lands must not cost another leaderboard call.
  const leaders = useMemo(() => mapLeaderboard(leadersRaw, rows), [leadersRaw, rows]);

  const isEmpty = !summary && rows.length === 0 && leaders.length === 0;

  // The Total Credits / Total Cost pair are two views of the same figure, each pinned to its unit.
  const kpiStats = summary
    ? [
        { label: 'Total Credits Used', value: formatCredits(summary.totalCreditsUsed) },
        // Prefer the API's own metered cost (`approx_total_cost_usd`); derive from credits only
        // when it isn't reported, since the derived figure is a rate approximation.
        {
          label: 'Total Cost',
          value:
            summary.totalCost != null
              ? formatUsd(summary.totalCost)
              : formatCost(summary.totalCreditsUsed),
        },
        { label: 'Total Interactions', value: summary.totalInteractions },
        { label: 'Active Orgs', value: summary.activeOrgs },
      ]
    : [];

  // Per-org user/message counts aren't in the fast rollup (they need the per-school fan-out) —
  // they live in the drill-in trust overview, so this list shows Credits + % Allowance only.
  const platformColumns = [
    { key: 'name', label: 'Organisation Name', width: '30%', render: (r) => <OrgName>{r.name}</OrgName> },
    { key: 'type', label: 'Type', width: '10%', render: (r) => <TypeBadge type={r.type} /> },
    { key: 'credits', label: 'Credits Used', width: '10%', render: (r) => format(r.credits) },
    {
      key: 'allowance',
      label: '% Allowance Used',
      width: '40%',
      render: (r) => <ProgressBar pct={r.allowancePct} />,
    },
    {
      key: 'action',
      label: 'Action',      
      width: '10%',
      render: (r) => (
        <PillButton type='button' onClick={() => history.push(drillPath(r))}>
          View
        </PillButton>
      ),
    },
  ];

  const leaderColumns = [
    { key: 'name', label: 'School Name', width: '250px', render: (r) => <OrgName>{r.name}</OrgName> },
    { key: 'group', label: 'Group', width: '180px', render: (r) => r.group || '—' },
    { key: 'messages', label: 'Messages', width: '100px', render: (r) => <Teal>{r.messages}</Teal> },
    { key: 'credits', label: 'Credits', width: '100px', render: (r) => format(r.credits) },
    {
      key: 'used',
      label: '% Used',
      // Unknown (this endpoint sends no allowance) is shown as a dash — a 0% bar read as "used
      // nothing", which is a different and wrong claim.
      render: (r) => (r.usagePct == null ? '—' : <ProgressBar pct={r.usagePct} small />),
    },
  ];

  return (
    <Stack data-testid='platform-ai-usage-section'>
      <SectionHeaderRow title='Platform Overview' size='lg'>
        <CreditCostToggle value={mode} onChange={setMode} />
      </SectionHeaderRow>

      <SectionStates loading={loading} error={error} isEmpty={isEmpty} onRetry={load} />

      {!loading && !error && !isEmpty && (
        <>
          {summary && (
            <div data-testid='platform-overview-tiles'>
              <KpiBar stats={kpiStats} />
            </div>
          )}

          <div>
            <SectionHeaderRow
              title='Platform Usage'
              subtitle='Across all connected organisations'
              align='end'
            >
              <SelectPill value={period} onChange={setPeriod} options={months} ariaLabel='Month' testId='month-dropdown' />
            </SectionHeaderRow>
            <div style={{ marginTop: sa.space.section }}>
              <DataTable
                columns={platformColumns}
                rows={rows}
                getRowKey={(r) => `${r.type}-${r.id}`}                            
                empty='No organisations have recorded usage this period.'
              />
              {(offset > 0 || rows.length === PAGE_SIZE) && (
                <Pager data-testid='platform-pager'>
                  <span>
                    {rows.length ? offset + 1 : offset}–{offset + rows.length}
                  </span>
                  <PagerBtn type='button' onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))} disabled={offset === 0}>
                    Prev
                  </PagerBtn>
                  <PagerBtn type='button' onClick={() => setOffset((o) => o + PAGE_SIZE)} disabled={rows.length < PAGE_SIZE}>
                    Next
                  </PagerBtn>
                </Pager>
              )}
            </div>
          </div>

          <div>
            <SectionHeaderRow title='Schools Leaderboard'>
              <span style={{ fontSize: 13, color: sa.color.textGray }}>Sort by:</span>
              <SelectPill
                value={sortKey}
                onChange={setSortKey}
                options={sortOptions}
                size='sm'
                ariaLabel='Sort by'
                testId='sort-by-dropdown'
              />
            </SectionHeaderRow>
              <DataTable
                columns={leaderColumns}
                rows={leaders}
                getRowKey={(r) => r.id}
                size='sm'
                //pageSize={10}
                //pagination={true}
                empty='No leaderboard data for this period.'                
              />              
          </div>
        </>        
      )}
    </Stack>
  );
}

function PlatformAiUsageSection() {
  return (
    <CreditModeProvider>
      <PlatformInner />
    </CreditModeProvider>
  );
}

export default PlatformAiUsageSection;
